import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { SITE_ORIGIN } from "@/data/site";
import { MAIN_SITE_URL } from "@/data/categories";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";

const PUBLISHED = "2026-01-20";
const UPDATED = "2026-08-19";

const TABLE_ROWS: [string, string, string][] = [
  ["table.r1a", "table.r1b", "table.r1c"],
  ["table.r2a", "table.r2b", "table.r2c"],
  ["table.r3a", "table.r3b", "table.r3c"],
  ["table.r4a", "table.r4b", "table.r4c"],
  ["table.r5a", "table.r5b", "table.r5c"],
];

const RESEARCH_BULLETS = ["research.b1", "research.b2", "research.b3", "research.b4"];
const FAQ_ITEMS = [1, 2, 3, 4];

export default function Article() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  const url = `${SITE_ORIGIN}/${currentLang}/basetao-spreadsheet`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t("h1"),
    description: t("meta.description"),
    author: { "@type": "Organization", name: t("author") },
    publisher: { "@type": "Organization", name: t("brand.name") },
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    mainEntityOfPage: url,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((n) => ({
      "@type": "Question",
      name: t(`artfaq.q${n}`),
      acceptedAnswer: { "@type": "Answer", text: t(`artfaq.a${n}`) },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("nav.home"),
        item: `${SITE_ORIGIN}/${currentLang}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("h1"),
        item: url,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd id="ld-article" data={JSON.stringify(articleSchema)} />
      <JsonLd id="ld-faq" data={JSON.stringify(faqSchema)} />
      <JsonLd id="ld-breadcrumb" data={JSON.stringify(breadcrumbSchema)} />

      <Navbar variant="solid" />

      {/* Article header */}
      <header className="bg-background-100 pt-24 pb-10 md:pt-28 md:pb-14">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to={`/${currentLang}`} className="transition hover:text-primary-700">
              {t("nav.home")}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="truncate text-foreground-700">{t("cta.readGuide")}</span>
          </nav>

          <h1 className="mt-6 text-2xl font-semibold leading-tight text-foreground-950 md:text-4xl">
            {t("h1")}
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            {t("intro")}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {t("author")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {t("published")}: {PUBLISHED}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-refresh-line"></i>
              {t("updated")}: {UPDATED}
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <article className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
        {/* What */}
        <section>
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("what.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("what.p1")}
          </p>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("what.p2")}
          </p>
        </section>

        {/* Research */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("research.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("research.p1")}
          </p>
          <ul className="mt-5 space-y-3">
            {RESEARCH_BULLETS.map((key) => (
              <li
                key={key}
                className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700"
              >
                <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-xs"></i>
                </span>
                {t(key)}
              </li>
            ))}
          </ul>
        </section>

        {/* Categories */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("cats.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("cats.p1")}
          </p>
        </section>

        {/* QC */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("qc.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("qc.p1")}
          </p>
        </section>

        {/* Shipping */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("shipping.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("shipping.p1")}
          </p>
        </section>

        {/* Comparison table */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("table.title")}
          </h2>
          <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full min-w-[540px] border-collapse text-sm">
              <thead>
                <tr className="bg-primary-50 text-left">
                  <th className="px-4 py-3 font-semibold text-foreground-900">
                    {t("table.col1")}
                  </th>
                  <th className="px-4 py-3 font-semibold text-primary-800">
                    {t("table.col2")}
                  </th>
                  <th className="px-4 py-3 font-semibold text-foreground-700">
                    {t("table.col3")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((row, i) => (
                  <tr
                    key={row[0]}
                    className={i % 2 === 0 ? "bg-background-50" : "bg-background-100"}
                  >
                    <td className="px-4 py-3 font-medium text-foreground-900">
                      {t(row[0])}
                    </td>
                    <td className="px-4 py-3 text-primary-800">{t(row[1])}</td>
                    <td className="px-4 py-3 text-foreground-600">{t(row[2])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("artfaq.title")}
          </h2>
          <div className="mt-5 space-y-4">
            {FAQ_ITEMS.map((n) => (
              <details
                key={n}
                className="group rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-foreground-900 md:text-base">
                  {t(`artfaq.q${n}`)}
                  <i className="ri-add-line text-lg text-primary-600 transition group-open:rotate-45"></i>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  {t(`artfaq.a${n}`)}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-lg bg-primary-50 p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("cta.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-700">
            {t("cta.text")}
          </p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-primary-600"
          >
            {t("cta.button")}
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </section>

        {/* Related / internal link */}
        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="text-base font-semibold text-foreground-950">
            {t("related.title")}
          </h2>
          <p className="mt-2 text-sm text-foreground-600">{t("related.text")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center gap-2 rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 whitespace-nowrap cursor-pointer transition hover:bg-primary-500 hover:text-background-50"
            >
              <i className="ri-arrow-left-line"></i>
              {t("cta.backHome")}
            </Link>
            <Link
              to={`/${currentLang}#categories`}
              className="inline-flex items-center gap-2 rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 whitespace-nowrap cursor-pointer transition hover:bg-primary-500 hover:text-background-50"
            >
              {t("categories.title")}
            </Link>
          </div>
        </section>

        {/* Disclaimer */}
        <p className="mt-8 text-xs leading-relaxed text-foreground-500">
          {t("disclaimer")}
        </p>
      </article>

      <Footer />
    </div>
  );
}